import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import type { AppLanguage, User, UserSettings } from '../types';
import { hasFirebaseConfig } from '../lib/firebase';
import { useAppSettings } from './AppSettingsProvider';
import {
  changeCurrentUserPassword,
  requestPasswordReset,
  signInWithEmail,
  signInWithGooglePopup,
  signOutCurrentUser,
  signUpWithEmail,
  subscribeToAuthState,
} from '../services/auth';
import {
  ensureUserProfile,
  updateUserLanguage,
  updateUserSettings,
} from '../services/userProfile';

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  isConfigured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (displayName: string, email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  saveUserSettings: (updates: Partial<UserSettings>) => Promise<void>;
  saveUserLanguage: (lang: AppLanguage) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { currentLanguage, switchLanguage } = useAppSettings();
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const currentLanguageRef = useRef(currentLanguage);

  useEffect(() => {
    currentLanguageRef.current = currentLanguage;
  }, [currentLanguage]);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = subscribeToAuthState((nextFirebaseUser) => {
      setFirebaseUser(nextFirebaseUser);

      if (!nextFirebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      void ensureUserProfile(nextFirebaseUser)
        .then((profile) => {
          if (cancelled) {
            return;
          }

          setUser(profile);
          if (profile.lang !== currentLanguageRef.current) {
            switchLanguage(profile.lang);
          }
        })
        .catch((error) => {
          console.error('Failed to load user profile:', error);
          if (!cancelled) {
            setUser(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [switchLanguage]);

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      user,
      loading,
      isConfigured: hasFirebaseConfig,
      signIn: async (email, password) => {
        await signInWithEmail(email, password);
      },
      signUp: async (displayName, email, password) => {
        const userCredential = await signUpWithEmail(email, password, displayName);
        const profile = await ensureUserProfile(userCredential.user);

        setFirebaseUser(userCredential.user);
        setUser(profile);
        setLoading(false);

        if (profile.lang !== currentLanguageRef.current) {
          switchLanguage(profile.lang);
        }
      },
      signInWithGoogle: async () => {
        await signInWithGooglePopup();
      },
      resetPassword: async (email) => {
        await requestPasswordReset(email);
      },
      logout: async () => {
        await signOutCurrentUser();
      },
      changePassword: async (currentPassword, newPassword) => {
        await changeCurrentUserPassword(currentPassword, newPassword);
      },
      saveUserSettings: async (updates) => {
        if (!user) {
          return;
        }

        const nextUser = await updateUserSettings(user, updates);
        setUser(nextUser);
      },
      saveUserLanguage: async (lang) => {
        if (!user) {
          switchLanguage(lang);
          return;
        }

        switchLanguage(lang);
        const nextUser = await updateUserLanguage(user, lang);
        setUser(nextUser);
      },
    }),
    [firebaseUser, loading, switchLanguage, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
